import { useState, useEffect } from 'react';
import { dashboardApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Wrench, Activity, Scissors, Truck } from 'lucide-react';
import { DeliveryEventCard } from './calendar/DeliveryEventCard';
import { PrepList } from './calendar/PrepList';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EVENT_ICONS = { maintenance: Wrench, activity: Activity, turf_cut: Scissors, delivery: Truck };

export function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  useEffect(() => {
    loadEvents();
  }, [monthStr]);

  const loadEvents = async () => {
    try {
      const res = await dashboardApi.getCalendarEvents(monthStr);
      setEvents(res.data);
    } catch {
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDay(new Date().getDate()); };

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const getDayEvents = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const selectedDayStr = selectedDay ? `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}` : null;
  const selectedEvents = selectedDay ? getDayEvents(selectedDay) : [];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  if (loading) return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="calendar-loading" />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Farm Calendar</h1>
          <p className="text-muted-foreground">Maintenance schedules, activities, turf cuts and customer deliveries</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 text-xs flex-wrap">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500" /> Maintenance</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-600" /> Activity</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500" /> Turf Cut</span>
            <span className="flex items-center gap-1" data-testid="legend-delivery"><span className="w-3 h-3 rounded-full bg-blue-600" /> Delivery</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-500" /> Delivered</span>
          </div>
        </div>
      </div>

      {selectedDay && <PrepList events={selectedEvents} dateLabel={selectedDayStr === todayStr ? 'Today' : `${MONTHS[month]} ${selectedDay}`} />}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month-btn">
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <CardTitle className="font-heading text-xl" data-testid="calendar-month-title">
                    {MONTHS[month]} {year}
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month-btn">
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
                <Button variant="outline" size="sm" onClick={goToday} data-testid="today-btn">Today</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                {/* Day headers */}
                {DAYS.map(d => (
                  <div key={d} className="bg-secondary/50 p-2 text-center text-xs font-semibold text-muted-foreground">{d}</div>
                ))}
                {/* Day cells */}
                {cells.map((day, idx) => {
                  if (!day) return <div key={`empty-${idx}`} className="bg-card p-2 min-h-[80px]" />;
                  const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayEvents = getDayEvents(day);
                  const isToday = dayStr === todayStr;
                  const isSelected = day === selectedDay;
                  return (
                    <div
                      key={day}
                      className={`bg-card p-1.5 min-h-[80px] cursor-pointer hover:bg-secondary/30 transition-colors ${isSelected ? 'ring-2 ring-primary ring-inset' : ''}`}
                      onClick={() => setSelectedDay(day)}
                      data-testid={`calendar-day-${day}`}
                    >
                      <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-primary text-primary-foreground' : ''}`}>
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map((ev) => (
                          <div
                            key={ev.id}
                            className="text-[10px] leading-tight px-1 py-0.5 rounded truncate text-white"
                            style={{ backgroundColor: ev.color }}
                          >
                            {ev.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <p className="text-[10px] text-muted-foreground px-1">+{dayEvents.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side panel - selected day events */}
        <div>
          <Card className="sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                {selectedDay ? `${MONTHS[month]} ${selectedDay}` : 'Select a day'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedDay ? (
                <p className="text-sm text-muted-foreground">Click a day on the calendar to see events</p>
              ) : selectedEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No events on this day</p>
              ) : (
                <div className="space-y-3">
                  {selectedEvents.map((ev) => {
                    if (ev.type === 'delivery') return <DeliveryEventCard key={ev.id} event={ev} />;
                    const Icon = EVENT_ICONS[ev.type] || Activity;
                    return (
                      <div key={ev.id} className="flex gap-3 p-3 rounded-lg border bg-card" data-testid={`event-${ev.id}`}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: ev.color }}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{ev.title}</p>
                          <Badge variant="outline" className="text-[10px] mt-1 capitalize">{ev.type.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
